// Page modèle : la moto détourée en 360°, dans chacun de ses coloris.
//
// - Rotation : glisser sur la moto (vers la gauche, l'avant vient vers soi) ou curseur
//   (clavier). Départ de trois quarts (vue 8), comme les vignettes.
// - Coloris : les pastilles changent la photo à l'angle courant, avec un fondu entre
//   l'ancien et le nouveau coloris ; nom, finition et prix suivent. /…/?coloris=m04
//   ouvre un coloris précis (l'adresse suit le coloris affiché).
// - Chargement : la vue affichée d'abord, puis les plus proches ; au survol d'une
//   pastille, la vue courante de ce coloris est préparée.
// - Un coloris dont les masques ne sont pas encore calculés reste sélectionnable : prix
//   et nom s'affichent, la photo garde le coloris précédent.

import { CutoutLoader } from "./cutout.js";
import { FRAME_COUNT as N, frameUrl, getCatalog } from "./frames.js";
import { nextBlend, paint, spring } from "./stage.js";

const root = document.querySelector("[data-model-viewer]");
if (root) viewer(root);

function viewer(root) {
  const catalog = getCatalog();
  const { model } = catalog;
  const width = matchMedia("(max-width: 700px)").matches ? 800 : 1200;
  const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;

  const canvas = root.querySelector(".model-canvas");
  const ctx = canvas.getContext("2d");
  const ghost = root.querySelector(".model-ghost");
  const loadingEl = root.querySelector(".showroom-loading");
  const angle = root.querySelector(".model-angle input");
  const nameEl = root.querySelector("[data-color-name]");
  const trimEl = root.querySelector("[data-color-trim]");
  const priceEl = root.querySelector("[data-color-price]");
  const swatches = [...root.querySelectorAll(".swatch")];

  const colors = model.colors;
  const byCode = (code) => colors.find((c) => c.code === code);
  const loader = new CutoutLoader({ capacity: 48 });
  const key = (c, i) => `${c.code}:${i}:${width}`;
  const maskUrl = (c, f) => `/masks/${width}/${c.mask}-${String(f).padStart(2, "0")}.png?v=${catalog.masks}`;
  const euros = (n) => `${n.toLocaleString("fr-FR")} €`;

  const asked = byCode(new URLSearchParams(location.search).get("coloris"));
  let current = asked ?? byCode(model.defaultColor) ?? colors[0]; // coloris choisi
  let shown = current.mask ? current : (colors.find((c) => c.mask) ?? null); // coloris dessiné
  let from = null; // coloris précédent, pendant le fondu
  let xfade = 1;
  let frame = 7;
  let target = 7;
  let vel = 0;
  let blend = 0;
  let raf = 0;
  let last = 0;
  let dpr = 1;
  let failures = 0;

  // ------------------------------------------------------------ chargement

  const byDistance = (from) => [...Array(N).keys()].sort((a, b) => Math.abs(a - from) - Math.abs(b - from) || a - b);
  const load = (c, i, prio) =>
    loader.load(key(c, i), frameUrl(c, i + 1, width), maskUrl(c, i + 1), { prio, tag: c.code, fetchPriority: prio === 0 ? "high" : "auto" });

  function request(c) {
    if (!c?.mask) return;
    loader.keepOnly((job) => job.tag === c.code);
    byDistance(Math.round(target)).forEach((i, rank) =>
      load(c, i, rank).then(wake, (err) => {
        if (err?.cancelled) return;
        failures++;
        console.warn(err);
        wake();
      }),
    );
  }

  function nearest(c, i) {
    for (let d = 0; d < N; d++) {
      const a = loader.get(key(c, i - d)) ?? loader.get(key(c, i + d));
      if (a) return a;
    }
    return null;
  }

  // ------------------------------------------------------------ dessin

  function resize() {
    const r = canvas.getBoundingClientRect();
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.max(1, Math.round(r.width * dpr));
    canvas.height = Math.max(1, Math.round(r.height * dpr));
    fitGhost();
    draw();
  }

  function views(c) {
    const f = Math.min(N - 1, Math.max(0, frame));
    const i0 = Math.floor(f);
    const t = f - i0;
    const i1 = Math.min(i0 + 1, N - 1);
    const dom = nearest(c, t < 0.5 ? i0 : i1);
    const mix = (0.5 - Math.abs(t - 0.5)) * blend;
    const other = mix > 0.01 ? loader.get(key(c, t < 0.5 ? i1 : i0)) : null;
    return { dom, other, mix };
  }

  function draw() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const now = shown && views(shown);
    loadingEl.hidden = failures < N && !!now?.dom;
    loadingEl.textContent = failures >= N ? "Photos indisponibles pour le moment" : "Chargement de la moto";
    // Fondu entre coloris : l'ancien reste opaque dessous, le nouveau apparaît par-dessus.
    if (from && xfade < 1) {
      const before = views(from);
      if (before.dom) paint(ctx, { ...before, fade: 1, frameBox: from.box, dpr });
    }
    if (now?.dom) paint(ctx, { ...now, fade: from ? xfade : 1, frameBox: shown.box, dpr });
  }

  function tick(t) {
    raf = 0;
    const dt = Math.min(50, last ? t - last : 16) / 1000;
    last = t;
    if (reduce) [frame, vel] = [target, 0];
    else [frame, vel] = spring(frame, vel, target, dt);
    blend = reduce ? 0 : nextBlend(blend, vel, dt);
    // Changement de coloris : fondu dès que la vue du nouveau coloris est prête.
    if (shown !== current && current.mask && nearest(current, Math.round(frame))) {
      from = shown;
      shown = current;
      xfade = reduce ? 1 : 0;
    }
    if (from) {
      xfade = Math.min(1, xfade + dt / 0.3);
      if (xfade === 1) from = null;
    }
    draw();
    if (frame !== target || blend > 0 || from || (shown !== current && current.mask)) wake();
    else last = 0;
  }

  function wake() {
    if (!raf) raf = requestAnimationFrame(tick);
  }

  // Nom géant derrière la moto, ajusté à la largeur.
  function fitGhost() {
    ghost.style.fontSize = "";
    const max = root.clientWidth * 0.98;
    const w = ghost.scrollWidth;
    if (w > max) ghost.style.fontSize = `${(parseFloat(getComputedStyle(ghost).fontSize) * max) / w}px`;
  }

  // ------------------------------------------------------------ coloris

  function select(c, push = true) {
    current = c;
    nameEl.textContent = c.name;
    trimEl.textContent = c.trim ? `finition ${c.trim}` : "";
    priceEl.textContent = euros(c.price);
    for (const s of swatches) s.setAttribute("aria-pressed", String(s.dataset.color === c.code));
    canvas.setAttribute("aria-label", `${model.name}, coloris ${c.name}, vue à 360°`);
    if (push) {
      const url = new URL(location.href);
      url.searchParams.set("coloris", c.code);
      history.replaceState(history.state, "", url);
    }
    request(c);
    wake();
  }

  for (const s of swatches) {
    const c = byCode(s.dataset.color);
    s.addEventListener("click", () => select(c));
    // Survol ou focus : on prépare la vue courante de ce coloris.
    const warm = () => c.mask && load(c, Math.round(target), 50).catch(() => {});
    s.addEventListener("pointerenter", warm);
    s.addEventListener("focus", warm);
  }

  // ------------------------------------------------------------ rotation

  const setTarget = (v) => {
    target = Math.min(N - 1, Math.max(0, v));
    angle.value = String(Math.round(target) + 1);
    wake();
  };
  angle.addEventListener("input", () => setTarget(Number(angle.value) - 1));

  // Glisser : toute la largeur de la scène fait le quart de tour complet.
  let drag = null;
  canvas.addEventListener("pointerdown", (e) => {
    drag = { x: e.clientX, y: e.clientY, start: target, id: e.pointerId, horizontal: e.pointerType === "mouse" };
    if (drag.horizontal) canvas.setPointerCapture(e.pointerId);
  });
  canvas.addEventListener("pointermove", (e) => {
    if (!drag || e.pointerId !== drag.id) return;
    const dx = e.clientX - drag.x;
    // Au doigt, on ne tourne que si le geste est horizontal (sinon, la page défile).
    if (!drag.horizontal) {
      if (Math.abs(dx) < 8 && Math.abs(e.clientY - drag.y) < 8) return;
      if (Math.abs(e.clientY - drag.y) > Math.abs(dx)) return void (drag = null);
      drag.horizontal = true;
      canvas.setPointerCapture(e.pointerId);
    }
    setTarget(drag.start - (dx / canvas.clientWidth) * (N - 1) * 1.2);
  });
  const end = () => (drag = null);
  canvas.addEventListener("pointerup", end);
  canvas.addEventListener("pointercancel", end);

  new ResizeObserver(resize).observe(canvas);
  document.fonts?.ready.then(fitGhost);
  select(current, false);
  if (shown && shown !== current) request(shown);
  resize();
}
